/*
  route-transition.js — custom close/rotate/open route transition controller.

  Outgoing: intercepts qualifying same-origin link clicks, plays CLOSE (panels
  cover the screen), then sets a sessionStorage flag and navigates so the route
  swap happens behind the white cover.

  Incoming: the inline <head> script (theme.liquid) reads the flag pre-paint and
  marks the document covering + held. This controller then plays ROTATE (seam
  sweeps 90deg) -> OPEN (panels part), releasing the entrance gate at 95% open.

  Pairs with route-transition.css (phase visuals + duration tokens). Excluded
  navigations (PLP-to-PLP, new tab, downloads, hashes, cross-origin, modified
  clicks) and reduced-motion fall through to normal navigation.
*/
(function () {
  'use strict';

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  let released = false;
  let inFlight = false;

  function durMs(name, fallback) {
    const raw = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
    const n = parseFloat(raw);
    if (!n) return fallback;
    return raw.indexOf('ms') !== -1 ? n : n * 1000;
  }

  function isPlpPath(path) {
    return path === '/search' || /^\/collections($|\/[^\/]+\/?$)/.test(path);
  }

  function release() {
    if (released) return;
    released = true;
    document.documentElement.removeAttribute('data-route-hold');
    document.dispatchEvent(new CustomEvent('routeentrance:release'));
  }

  function cleanup(overlay) {
    document.documentElement.removeAttribute('data-route-entering');
    if (overlay) {
      overlay.dataset.rtPhase = 'idle';
      overlay.style.pointerEvents = 'none';
    }
  }

  function runIncoming() {
    const html = document.documentElement;
    if (html.getAttribute('data-route-entering') !== 'true') return;

    const overlay = document.querySelector('[data-route-transition]');
    if (!overlay || REDUCED) {
      release();
      cleanup(overlay);
      return;
    }

    const rotate = durMs('--rt-rotate', 500);
    const open = durMs('--rt-open', 700);
    const stage = overlay.querySelector('[data-rt-stage]');

    overlay.style.pointerEvents = 'auto';

    // Panels are already covering (pre-paint). Kick ROTATE next frame so the
    // 0deg -> 90deg transition actually fires.
    requestAnimationFrame(function () {
      overlay.dataset.rtPhase = 'rotate';

      let advanced = false;
      const toOpen = function () {
        overlay.dataset.rtPhase = 'open';
        setTimeout(release, open * 0.95);
        setTimeout(function () { cleanup(overlay); }, open);
      };
      const onRotateEnd = function (event) {
        if (event.target !== stage || event.propertyName !== 'transform' || advanced) return;
        advanced = true;
        stage.removeEventListener('transitionend', onRotateEnd);
        toOpen();
      };

      stage.addEventListener('transitionend', onRotateEnd);
      // Safety net if transitionend is missed (interrupted/!supported).
      setTimeout(function () {
        if (advanced) return;
        advanced = true;
        stage.removeEventListener('transitionend', onRotateEnd);
        toOpen();
      }, rotate + 80);
    });
  }

  function qualifies(event) {
    if (inFlight || REDUCED || event.defaultPrevented) return null;
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
    const anchor = event.target.closest ? event.target.closest('a[href]') : null;
    if (!anchor || anchor.target === '_blank' || anchor.hasAttribute('download')) return null;
    if (anchor.closest('[data-no-route-transition]')) return null; // opt-out (e.g. listing pagination)
    const raw = anchor.getAttribute('href');
    if (!raw || raw.charAt(0) === '#') return null;
    let url;
    try {
      url = new URL(anchor.href, location.href);
    } catch {
      return null;
    }
    if (url.origin !== location.origin) return null;
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null;
    if (url.pathname === location.pathname && url.hash) return null; // same-page anchor
    if (isPlpPath(location.pathname) && isPlpPath(url.pathname)) return null; // PLP -> PLP
    return url;
  }

  function onClick(event) {
    const url = qualifies(event);
    if (!url) return;
    event.preventDefault();
    inFlight = true;

    const overlay = document.querySelector('[data-route-transition]');
    const go = function () {
      try {
        sessionStorage.setItem('routeTransition', '1');
      } catch { /* ignore; navigate anyway */ }
      location.assign(url.href);
    };

    if (!overlay) {
      go();
      return;
    }
    overlay.style.pointerEvents = 'auto';
    overlay.dataset.rtPhase = 'close';
    setTimeout(go, durMs('--rt-close', 600));
  }

  function init() {
    runIncoming();
    document.addEventListener('click', onClick);
  }

  document.addEventListener('DOMContentLoaded', init);

  // Back/forward navigation restores the page from the bfcache, which does NOT
  // fire DOMContentLoaded - so the page can come back frozen in its outgoing
  // CLOSE (covering) state with inFlight still true (dead links). Reset the
  // overlay to idle and clear the gate whenever the page is shown from cache.
  window.addEventListener('pageshow', function (event) {
    if (!event.persisted) return;
    inFlight = false;
    released = false;
    const overlay = document.querySelector('[data-route-transition]');
    if (overlay) {
      overlay.dataset.rtPhase = 'idle';
      overlay.style.pointerEvents = 'none';
    }
    const html = document.documentElement;
    html.removeAttribute('data-route-hold');
    html.removeAttribute('data-route-entering');
  });
})();
